import { Box, Flex, Icon, IconButton, Text, Tooltip, useDisclosure } from '@chakra-ui/react';
import debounce from 'lodash/debounce';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CellValue } from 'react-table';

import { IconTrashbin } from 'assets/icons';
import Button from 'components/Button';
import { ConfirmationModal } from 'components/Modal';
import { ReactSelectAsyncPaginate } from 'components/ReactSelect';
import SearchField from 'components/SearchField';
import Table, { IndeterminateCheckbox } from 'components/Table';
import Title from 'components/Title';
import { useIntegrationSyncPolling } from 'hooks/common/useIntegrationSyncPolling';
import { useDeleteQuickbookOnlineEmployee } from 'hooks/integrations/useDeleteQuickbookOnlineEmployee';
import { useFetchQBOnlineTimeeroPaginatedEmployee } from 'hooks/integrations/useFetchPaginatedQBOnlineTimeeroEmployee';
import {
  IQBEmployeeSearchQuery,
  fetchQuickbookPaginatedEmployee,
} from 'hooks/integrations/useFetchPaginatedQuickbookEmployees';
import { useMapBulkQuickbookEmployee, IBulkQuickbookEmployee } from 'hooks/integrations/useMapBulkQuickbookEmployees';
import { useMapQuickbookEmployee, IQuickbookEmployee } from 'hooks/integrations/useMapQuickbookEmployee';
import { useSyncQuickbook } from 'hooks/integrations/useSyncQuickbookOnline';
import { useUnmapQuickbookEmployee, IQuickbookUnmapEmployee } from 'hooks/integrations/useUnmapQuickbookEmployees';
import { getAsyncSelectLoadOptions } from 'utils/helpers';
import toaster from 'utils/toaster';

import EmployeeMappingTableFooter from './EmployeeMappingTableFooter';

const initialSearchQuery: IQBEmployeeSearchQuery = {
  page: 1,
  page_size: 25,
  search: '',
};

const EmployeeMapping = () => {
  const [mappedUsers, setMappedUsers] = useState<any>({});
  const [dialogId, setDialogId] = useState<number | null>(null);
  const [currentMapUser, setCurrentMapUser] = useState<any>(null);
  const [searchQuery, setSearchQuery] = useState<IQBEmployeeSearchQuery>(initialSearchQuery);

  const { t } = useTranslation(['integrations', 'common', 'employees']);

  const { isOpen: isDeleteModalOpen, onOpen: onDeleteModalOpen, onClose: onDeleteModalClose } = useDisclosure();

  const {
    data: timeeroEmployeeData,
    status: timeeroEmployeeStatus,
    refetch,
  } = useFetchQBOnlineTimeeroPaginatedEmployee(searchQuery);
  const { status: qbMapEmployeeStatus, mutateAsync: qbMapEmployeeMutateAsync } = useMapQuickbookEmployee();
  const { status: qbUnmapEmployeeStatus, mutateAsync: qbUnmapEmployeeMutateAsync } = useUnmapQuickbookEmployee();
  const { status: qbMapBulkEmployeeStatus, mutateAsync: qbMapBulkEmployeeMutateAsync } = useMapBulkQuickbookEmployee();
  const { status: quickbookSyncStatus, mutateAsync: quickbookSyncMutateAsync, data: syncData } = useSyncQuickbook();
  const { status: employeeDeleteStatus, mutateAsync: employeeDeleteMutateAsync } = useDeleteQuickbookOnlineEmployee();

  const { syncLongPollingStatus } = useIntegrationSyncPolling({ id: syncData?.payload?.id, refetch });

  const columns = [
    {
      id: 'selection',
      width: 30,
      Header: ({ getToggleAllRowsSelectedProps }: any) => {
        return <IndeterminateCheckbox {...getToggleAllRowsSelectedProps()} colorScheme="secondary" />;
      },
      Cell: ({ row }: any) => {
        return <IndeterminateCheckbox {...row.getToggleRowSelectedProps()} colorScheme="secondary" />;
      },
    },
    {
      Header: t('integrations:timeeroEmployees'),
      accessor: 'name',
    },
    {
      Header: t('integrations:quickbooks.quickbooksEmployees'),
      accessor: 'quickbooksEmployee',
      Cell: (data: CellValue) => {
        return (
          <ReactSelectAsyncPaginate
            loadOptions={employeeLoadOptions}
            additional={{
              page: 1,
            }}
            value={
              data.row.original?.selected_user
                ? data.row.original?.selected_user || null
                : mappedUsers &&
                    mappedUsers[`${data.row.original.id}`] &&
                    mappedUsers[`${data.row.original.id}`].employee
                  ? mappedUsers[`${data.row.original.id}`].employee
                  : null
            }
            onChange={(value: any) => {
              setMappedUsers((prevValue: any) => ({
                ...prevValue,
                [data.row.original.id]: {
                  employee: value || null,
                },
              }));
            }}
            size="md"
            variant="filled"
            fontSize="sm"
            debounceTimeout={300}
            menuPortalTarget={document.body}
            isSearchable
            placeholder={t('integrations:selectEmployees')}
            getOptionLabel={(option: any) => option.firstName + ' ' + option.lastName}
            getOptionValue={(option: any) => option.id}
          />
        );
      },
    },
    {
      Header: '',
      accessor: 'first',
      Cell: (data: CellValue) => {
        return (
          <>
            {data.row.original?.selected_user ? (
              <>
                <Button
                  _hover={{ bg: 'secondary.100' }}
                  fontWeight="bold"
                  background="rgba(250, 85, 85, 0.2)"
                  color="rgba(250, 85, 85, 1)"
                  isLoading={qbUnmapEmployeeStatus === 'loading' && currentMapUser === data.row.original.id}
                  onClick={() => onUnmapEmployee(data.row.original.id)}
                >
                  {t('integrations:removeMapping')}
                </Button>
                <Tooltip label={t('integrations:deleteEmployee')} hasArrow bg="black">
                  <IconButton
                    aria-label="Delete Employee"
                    bg="rgba(255, 83, 83, 0.1)"
                    icon={<Icon as={IconTrashbin} />}
                    ml="2"
                    // size="md"
                    _hover={{ bg: 'rgba(255, 83, 83, 0.2)' }}
                    _active={{ bg: 'rgba(255, 83, 83, 0.2)' }}
                    onClick={() => handleDeleteEmployeeIconClick(data.row.original.selected_user?.id)}
                  />
                </Tooltip>
              </>
            ) : (
              <Button
                _hover={{ bg: 'rgba(255, 103, 32, 0.2)' }}
                background="rgba(255, 103, 32, 0.1)"
                colorScheme="rgba(255, 103, 32, 0.3)"
                color="secondary.500"
                isLoading={qbMapEmployeeStatus === 'loading' && currentMapUser === data.row.original.id}
                onClick={() => onMapEmployee(data.row.original.id)}
              >
                {t('integrations:mapEmployee')}
              </Button>
            )}
          </>
        );
      },
    },
  ];

  useEffect(
    () => {
      if (timeeroEmployeeStatus === 'success') {
        timeeroEmployeeData?.payload.forEach((employee: any) => {
          if (!mappedUsers.hasOwnProperty(employee.id) && employee.selected_user) {
            setMappedUsers((prevValue: any) => ({
              ...prevValue,
              [employee.id]: {
                employee: employee.selected_user,
              },
            }));
          }
        });
      }
    },
    // eslint-disable-next-line
    [timeeroEmployeeStatus]
  );

  const onQuicbookSync = () => {
    quickbookSyncMutateAsync();
    syncLongPollingStatus.current = 'fetching';
  };

  const onBulkEmployeeMap = (selected: any) => {
    let isAllMapped = true;
    const mappedUsersArray = [];
    for (let i = 0; i < selected.length; i++) {
      if (!mappedUsers.hasOwnProperty(selected[i])) {
        isAllMapped = false;
      } else {
        mappedUsersArray.push(mappedUsers[selected[i]]?.employee.id || mappedUsers[selected[i]].employee.id);
      }
    }
    if (!isAllMapped) {
      toaster.error({ description: t('integrations:employeeAllSelectedMapError') });
      return;
    }

    const postData: IBulkQuickbookEmployee = {
      quick_book_employee_ids: mappedUsersArray,
      user_ids: selected,
    };
    qbMapBulkEmployeeMutateAsync(postData).then(() => {
      refetch();
    });
  };

  const onMapEmployee = (timeeroId: number) => {
    if (mappedUsers && mappedUsers[timeeroId] && mappedUsers[timeeroId].hasOwnProperty('employee')) {
      setCurrentMapUser(timeeroId);
      const postData: IQuickbookEmployee = {
        quick_book_employee_id: parseInt(mappedUsers[timeeroId].employee.userId || mappedUsers[timeeroId].employee.id),
        user_id: timeeroId,
      };
      qbMapEmployeeMutateAsync(postData).then(() => {
        refetch();
      });
    } else {
      toaster.error({ description: t('integrations:employeeSelectMapError') });
    }
  };

  const onUnmapEmployee = (timeeroId: number) => {
    setCurrentMapUser(timeeroId);
    const postData: IQuickbookUnmapEmployee = {
      user_id: timeeroId,
    };
    qbUnmapEmployeeMutateAsync(postData).then(() => {
      const tempMappedUser = mappedUsers;
      delete tempMappedUser[timeeroId];
      setMappedUsers(tempMappedUser);
      refetch();
    });
  };

  const employeeLoadOptions = (search: any, _: any, { page }: any) => {
    return getAsyncSelectLoadOptions(fetchQuickbookPaginatedEmployee, { page, search });
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleSearchInputChange = useCallback(
    debounce((e: React.ChangeEvent<HTMLInputElement>) => {
      const searchValue = e.target.value.toLowerCase().trim();
      setSearchQuery({ ...searchQuery, page: 1, search: searchValue });
    }, 400),
    [searchQuery]
  );

  const handlePageChange = useCallback((selectedPageIndex: number) => {
    const updatedSearchQuery = (previousSearchQuery: IQBEmployeeSearchQuery) => ({
      ...previousSearchQuery,
      page: selectedPageIndex + 1,
    });
    setSearchQuery(updatedSearchQuery);
  }, []);

  const handlePageSizeChange = useCallback((pageSize: number) => {
    const updatedSearchQuery = (previousSearchQuery: IQBEmployeeSearchQuery) => ({
      ...previousSearchQuery,
      page_size: pageSize,
      page: 1,
    });
    setSearchQuery(updatedSearchQuery);
  }, []);

  const handleDeleteEmployeeIconClick = (id: number) => {
    setDialogId(id);
    onDeleteModalOpen();
  };

  const deleteEmployee = () => {
    employeeDeleteMutateAsync({ employee_id: dialogId! }).then(() => {
      setDialogId(null);
      onDeleteModalClose();
      setMappedUsers({});
      setCurrentMapUser(null);
      refetch();
    });
  };

  return (
    <Box>
      <Title>{t('integrations:quickbooks.onlineMappingHeading')}</Title>
      <Flex justifyContent="space-between">
        <Text variant="h2" fontSize="lg" ml={10} mt={4}>
          {t('integrations:mapFieldsPlatforms')}
        </Text>
        <Button
          colorScheme="secondary"
          mt={{ base: '5', md: '0' }}
          isLoading={quickbookSyncStatus === 'loading' || syncLongPollingStatus?.current === 'fetching'}
          onClick={onQuicbookSync}
        >
          {t('integrations:quickbooks.syncOnline')}
        </Button>
      </Flex>
      <Box ml="auto" mt={4} maxWidth={{ md: '250px' }} width="100%">
        <SearchField
          inputProps={{
            placeholder: t('common:searchUsers'),
            onChange: handleSearchInputChange,
          }}
        />
      </Box>

      <ConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={onDeleteModalClose}
        title={t('employees:deleteUserTitle')}
        subTitle=""
        confirmButtonLabel={t('common:delete')}
        confirmButtonProps={{
          bg: 'rgba(255, 83, 83, 0.1)',
          color: 'secondary.500',
          _hover: {
            bg: 'rgba(255, 83, 83, 0.2)',
          },
          _active: {
            bg: 'rgba(255, 83, 83, 0.2)',
          },
          isLoading: employeeDeleteStatus === 'loading',
          onClick: deleteEmployee,
        }}
      />

      <Table
        columns={columns}
        data={timeeroEmployeeData?.payload || []}
        isLoading={timeeroEmployeeStatus === 'loading'}
        enableRowSelect
        paginationData={timeeroEmployeeData?.meta_data?.pagination}
        onPageChange={handlePageChange}
        handlePageSizeChange={handlePageSizeChange}
        paginationStyle="rowsPerPage"
        renderTableFooter={(selectedFlatRows: any, toggleAllRowSelected) => {
          return (
            <EmployeeMappingTableFooter
              selectedFlatRows={selectedFlatRows}
              toggleAllRowSelected={toggleAllRowSelected}
              handleBulkEmployeeMap={onBulkEmployeeMap}
              isLoading={qbMapBulkEmployeeStatus === 'loading'}
              t={t}
            />
          );
        }}
      />
    </Box>
  );
};
export default EmployeeMapping;
